import Trans from 'next-translate/Trans';
import { forwardRef, useEffect, useRef, useState } from 'react';

import Turnstile, { type TurnstileHandle } from '@/components/Turnstile';

/**
 * The auth forms' Turnstile: invisible unless Cloudflare needs a click
 * (`interaction-only`), with a translated hint when it can't load. It takes
 * no space while hidden and adds its own gap below once it shows, so the
 * form doesn't keep an empty row for it.
 */
const AuthTurnstile = forwardRef<
  TurnstileHandle,
  { action: string; className?: string }
>(function AuthTurnstile({ action, className }, ref) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const box = boxRef.current;
    if (!box || !('ResizeObserver' in window)) return;
    const observer = new ResizeObserver(([entry]) => {
      setVisible((entry?.contentRect.height ?? 0) > 0);
    });
    observer.observe(box);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={boxRef} className={visible ? className : undefined}>
      <Turnstile
        ref={ref}
        action={action}
        appearance='interaction-only'
        failureHint={
          <p className='mt-2 max-w-[340px] text-center text-[13px] leading-5 text-amber-800 dark:text-amber-200'>
            <Trans
              i18nKey="The security check didn't load. It's usually an ad blocker, a privacy extension or a work network blocking Cloudflare. <0>Contact us</0> and we'll sign you in."
              ns='login'
              defaultTrans="The security check didn't load. It's usually an ad blocker, a privacy extension or a work network blocking Cloudflare. <0>Contact us</0> and we'll sign you in."
              components={[
                <a
                  key='contact'
                  href='mailto:hey@easy-peasy.ai?subject=Cannot%20sign%20in%20-%20security%20check'
                  className='font-semibold underline underline-offset-2'
                />,
              ]}
            />
          </p>
        }
      />
    </div>
  );
});

export default AuthTurnstile;
